<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IMAGE_TYPES, pickImages } from '../assistantFormat';

const props = defineProps({
  busy: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  canStop: { type: Boolean, default: true },
});
const emit = defineEmits(['send', 'stop', 'rejected']);

const { t } = useI18n();
const text = ref('');
const files = ref([]);
const fileInput = ref(null);
const input = ref(null);
const previews = ref([]);

watch(files, (next, previous = []) => {
  previous.forEach((_, index) => URL.revokeObjectURL(previews.value[index]));
  previews.value = next.map(file => URL.createObjectURL(file));
});
onBeforeUnmount(() => previews.value.forEach(url => URL.revokeObjectURL(url)));

const canSend = computed(
  () =>
    !props.busy && !props.disabled && (text.value.trim() || files.value.length)
);

const addFiles = incoming => {
  const { accepted, rejected } = pickImages(files.value, incoming);
  files.value = accepted;
  if (rejected) emit('rejected');
};
const removeFile = index => {
  files.value = files.value.filter((_, i) => i !== index);
};
const onPaste = event => {
  const pasted = [...(event.clipboardData?.files || [])];
  if (pasted.length) {
    event.preventDefault();
    addFiles(pasted);
  }
};
const onDrop = event => addFiles(event.dataTransfer?.files || []);
const onFileChange = event => {
  addFiles(event.target.files);
  event.target.value = '';
};

// The box grows with the text up to its max height, then scrolls.
const grow = () => {
  const el = input.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};
const send = () => {
  if (!canSend.value) return;
  emit('send', { content: text.value.trim(), files: files.value });
  text.value = '';
  files.value = [];
  nextTick(grow);
};
// A suggestion from the empty chat fills the box for the user to edit or send.
const fill = value => {
  text.value = value;
  nextTick(() => {
    grow();
    input.value?.focus();
  });
};
defineExpose({ fill });
const onKeydown = event => {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    send();
  }
};
</script>

<template>
  <!-- On phones Chatwoot's menu button floats at the bottom start corner: keep clear of it. -->
  <div
    class="px-3 pt-2 pb-3 md:px-4 md:pb-4"
    :class="{ 'max-md:ps-[4.75rem]': !compact }"
    @dragover.prevent
    @drop.prevent="onDrop"
  >
    <div
      class="rounded-2xl border bg-n-solid-1 shadow-sm transition-colors border-n-weak focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/15"
    >
      <div v-if="files.length" class="flex flex-wrap gap-2 px-3 pt-3">
        <div v-for="(file, index) in files" :key="index" class="relative">
          <img
            :src="previews[index]"
            :alt="file.name"
            class="object-cover rounded-xl size-16 border border-n-weak"
          />
          <button
            type="button"
            class="absolute -top-1.5 -end-1.5 flex items-center justify-center rounded-full size-5 bg-n-slate-12 text-n-solid-1 shadow"
            :aria-label="t('ZUWAD_ASSISTANT.REMOVE_IMAGE')"
            @click="removeFile(index)"
          >
            <span class="i-lucide-x size-3" />
          </button>
        </div>
      </div>
      <div class="flex items-end gap-1.5 p-2">
        <button
          type="button"
          class="flex items-center justify-center shrink-0 rounded-xl size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-50"
          :disabled="disabled"
          :aria-label="t('ZUWAD_ASSISTANT.ATTACH')"
          :title="t('ZUWAD_ASSISTANT.ATTACH')"
          @click="fileInput.click()"
        >
          <span class="i-lucide-image-plus size-5" />
        </button>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          :accept="IMAGE_TYPES.join(',')"
          @change="onFileChange"
        />
        <!-- Chatwoot styles every textarea as a field; this one sits inside the box instead. -->
        <textarea
          ref="input"
          v-model="text"
          dir="auto"
          rows="1"
          :disabled="disabled"
          :placeholder="t('ZUWAD_ASSISTANT.PLACEHOLDER')"
          class="flex-1 min-w-0 !h-auto min-h-10 resize-none !border-0 !outline-none !bg-transparent !rounded-none !px-1 py-2.5 text-[15px] leading-5 text-n-slate-12 placeholder:text-n-slate-10 !mb-0"
          :class="compact ? 'max-h-32' : 'max-h-60'"
          @input="grow"
          @keydown="onKeydown"
          @paste="onPaste"
        />
        <button
          v-if="busy"
          type="button"
          class="flex items-center justify-center shrink-0 rounded-full size-10 bg-n-slate-12 text-n-solid-1 hover:opacity-90 disabled:opacity-40"
          :disabled="!canStop"
          :aria-label="t('ZUWAD_ASSISTANT.STOP')"
          :title="t('ZUWAD_ASSISTANT.STOP')"
          @click="emit('stop')"
        >
          <span class="i-ph-stop-fill size-4" />
        </button>
        <button
          v-else
          type="button"
          class="flex items-center justify-center shrink-0 rounded-full size-10 bg-n-brand text-white shadow-sm hover:brightness-110 disabled:bg-n-alpha-3 disabled:text-n-slate-10 disabled:shadow-none"
          :disabled="!canSend"
          :aria-label="t('ZUWAD_ASSISTANT.SEND')"
          :title="t('ZUWAD_ASSISTANT.SEND')"
          @click="send"
        >
          <span class="i-lucide-arrow-up size-5" />
        </button>
      </div>
    </div>
    <p
      v-if="!compact"
      class="mt-1.5 text-center text-xxs text-n-slate-10 max-md:hidden"
    >
      {{ t('ZUWAD_ASSISTANT.COMPOSER_HINT') }}
    </p>
  </div>
</template>
